import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Plus, FileText, CheckCircle, Clock, TrendingUp, Users, Upload, Heart, Eye } from 'lucide-react';

const NGODashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [intents, setIntents] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('projects');

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const checkAuth = async () => {
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');
    
    if (!token || !userData) {
      navigate('/xchange/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'ngo') {
      navigate('/xchange/login');
      return;
    }

    setUser(parsedUser);
    await fetchDashboardData(token);
  };

  const fetchDashboardData = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      // Fetch my projects
      const projectsRes = await fetch(`${backendUrl}/api/xchange/projects/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectsData = await projectsRes.json();
      setProjects(projectsData);

      // Fetch all intents
      const intentsRes = await fetch(`${backendUrl}/api/xchange/intents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const intentsData = await intentsRes.json();
      setIntents(intentsData);

      // Fetch my matches
      const matchesRes = await fetch(`${backendUrl}/api/xchange/matches/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const matchesData = await matchesRes.json();
      setMatches(matchesData);

      // Calculate basic stats from projects
      const statsData = {
        total_projects: projectsData.length,
        approved_projects: 0,
        total_funding_received: 0,
        total_beneficiaries: 0
      };
      setStats(statsData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    // Navigate to project creation form (to be implemented)
    alert('Project creation form will be implemented');
  };

  const handleExpressInterest = async (intentId) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/xchange/interest/express`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ intentId })
      });

      if (response.ok) {
        alert('Interest expressed successfully! We will notify you if there is mutual interest.');
        // Refresh data
        await fetchDashboardData(token);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to express interest');
      }
    } catch (error) {
      console.error('Error expressing interest:', error);
      alert('An error occurred while expressing interest');
    }
  };

  const getMatchStatusBadge = (status) => {
    const styles = {
      pending_admin: 'bg-yellow-100 text-yellow-800',
      connected: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending_admin: 'Pending Admin Review',
      connected: 'Connected',
      closed: 'Closed'
    };
    return { style: styles[status] || 'bg-gray-100 text-gray-800', label: labels[status] || status };
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-800',
      'submitted': 'bg-blue-100 text-blue-800',
      'under_review': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'active': 'bg-teal-100 text-teal-800',
      'completed': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{borderColor: '#14B8A6'}}></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{color: '#1E3A5F'}}>
                NGO Dashboard
              </h1>
              <p className="text-gray-600">Welcome back, {user?.organization}</p>
            </div>
            <button
              onClick={() => navigate('/saas/agency/dashboard')}
              className="px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
              style={{
                background: '#14B8A6',
                color: '#FFFFFF'
              }}
            >
              View Project Management →
            </button>
          </div>

          {/* Quick Access: Project Management Hub */}
          <div className="mb-8 bg-gradient-to-r from-[#14B8A6] to-[#0D9488] rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Project Management</h3>
                <p className="text-white/90 text-sm mb-4">
                  Manage projects, submit updates, track milestones, and communicate with sponsors
                </p>
                <button
                  onClick={() => navigate('/project-management')}
                  className="px-6 py-2 bg-white rounded-lg font-semibold hover:shadow-lg transition-all"
                  style={{color: '#14B8A6'}}
                >
                  Access Project Hub →
                </button>
              </div>
              <div className="hidden md:block">
                <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center">
                  <TrendingUp className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          {stats && (
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                    <FileText className="w-6 h-6" style={{color: '#14B8A6'}} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>{projects.length}</div>
                <div className="text-sm text-gray-600">My Projects</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(30, 58, 95, 0.1)'}}>
                    <Eye className="w-6 h-6" style={{color: '#1E3A5F'}} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>{intents.length}</div>
                <div className="text-sm text-gray-600">Available Intents</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(42, 131, 120, 0.1)'}}>
                    <Heart className="w-6 h-6" style={{color: '#2A8378'}} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>{matches.length}</div>
                <div className="text-sm text-gray-600">Total Matches</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: 'rgba(20, 184, 166, 0.1)'}}>
                    <CheckCircle className="w-6 h-6" style={{color: '#14B8A6'}} />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>
                  {matches.filter(m => m.status === 'connected').length}
                </div>
                <div className="text-sm text-gray-600">Active Connections</div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('projects')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'projects'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'projects' ? {borderColor: '#14B8A6', color: '#14B8A6'} : {}}
              >
                My Projects ({projects.length})
              </button>
              <button
                onClick={() => setActiveTab('intents')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'intents'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'intents' ? {borderColor: '#14B8A6', color: '#14B8A6'} : {}}
              >
                Browse Intents ({intents.length})
              </button>
              <button
                onClick={() => setActiveTab('matches')}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  activeTab === 'matches'
                    ? 'border-b-2 text-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'matches' ? {borderColor: '#14B8A6', color: '#14B8A6'} : {}}
              >
                My Matches ({matches.length})
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {/* My Projects Tab */}
              {activeTab === 'projects' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold" style={{color: '#1E3A5F'}}>My Projects</h3>
                    <button
                      onClick={handleCreateProject}
                      className="px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all hover:shadow-lg"
                      style={{background: '#14B8A6', color: '#FFFFFF'}}
                    >
                      <Plus size={20} />
                      Create New Project
                    </button>
                  </div>

                  {projects.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 mb-4">No projects yet. Create your first project to get started!</p>
                      <button
                        onClick={handleCreateProject}
                        className="px-6 py-3 rounded-lg font-semibold"
                        style={{background: '#14B8A6', color: '#FFFFFF'}}
                      >
                        Create First Project
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {projects.map((project) => (
                        <div key={project.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                          <h4 className="text-lg font-bold mb-2" style={{color: '#1E3A5F'}}>{project.title}</h4>
                          <p className="text-gray-600 mb-4">{project.summary}</p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="px-3 py-1 rounded-full bg-teal-100 text-teal-800">{project.sector}</span>
                            <span className="text-gray-600">📍 {project.state}</span>
                            <span className="text-gray-600">💰 ₹{project.fundingMin}L - ₹{project.fundingMax}L</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Browse Intents Tab */}
              {activeTab === 'intents' && (
                <div>
                  <h3 className="text-xl font-bold mb-6" style={{color: '#1E3A5F'}}>Available Corporate Intents</h3>

                  {intents.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600">No intents available at the moment</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {intents.map((intent) => (
                        <div key={intent.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h4 className="text-lg font-bold mb-1" style={{color: '#1E3A5F'}}>{intent.title}</h4>
                              <p className="text-sm text-gray-500">by {intent.corpName}</p>
                            </div>
                            <button
                              onClick={() => handleExpressInterest(intent.id)}
                              className="px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all hover:shadow-lg"
                              style={{background: '#14B8A6', color: '#FFFFFF'}}
                            >
                              <Heart size={16} />
                              Express Interest
                            </button>
                          </div>
                          <p className="text-gray-600 mb-4">{intent.summary}</p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                              {intent.sectors.join(', ')}
                            </span>
                            <span className="text-gray-600">📍 {intent.state}</span>
                            <span className="text-gray-600">💰 ₹{intent.budgetMin}L - ₹{intent.budgetMax}L</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* My Matches Tab */}
              {activeTab === 'matches' && (
                <div>
                  <h3 className="text-xl font-bold mb-6" style={{color: '#1E3A5F'}}>My Matches</h3>

                  {matches.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600">No matches yet. Express interest in intents to get started!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {matches.map((match) => {
                        const badge = getMatchStatusBadge(match.status);
                        return (
                          <div key={match.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-all">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-lg font-bold mb-1" style={{color: '#1E3A5F'}}>
                                  Match with {match.corpName}
                                </h4>
                                <p className="text-sm text-gray-500">
                                  {match.projectTitle || match.intentTitle}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.style}`}>
                                {badge.label}
                              </span>
                            </div>
                            {match.score > 0 && (
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="text-gray-600">Match Score</span>
                                  <span className="font-semibold" style={{color: '#14B8A6'}}>{match.score}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full"
                                    style={{background: '#14B8A6', width: `${match.score}%`}}
                                  ></div>
                                </div>
                              </div>
                            )}
                            <p className="text-sm text-gray-600">
                              {match.status === 'pending_admin' && 'Awaiting TMG admin review. You will be notified once the introduction is made.'}
                              {match.status === 'connected' && 'TMG has facilitated the introduction. Check your email for contact details.'}
                              {match.status === 'closed' && 'This match has been closed.'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NGODashboard;
